/**
 * Newsletter iframe module creation and resize handling
 *
 * Now uses the shared iframeResizer utility.
 *
 * @module lib/newsletter/iframe
 */

const { resolveNewsletterContext } = require('../utils/brandContext');
const { createIframeResizer } = require('../utils/iframeResizer');
const { buildNewsletterIframeUrl } = require('./urls');

// Create the newsletter iframe resizer using the shared factory
const ensureNewsletterIframeResizer = createIframeResizer({
  messageType: 'MILLA_NEWSLETTER_RESIZE',
  minHeight: 180,
  maxHeight: 1200
});

/**
 * Create a newsletter iframe module (DOM element)
 * @param {string} [location] - Where the widget is placed (e.g., 'sidebar', 'article_mid')
 * @returns {HTMLElement|null} The wrapper element containing the iframe
 */
function createNewsletterIframeModule(location) {
  if (typeof document === 'undefined') return null;
  ensureNewsletterIframeResizer();

  const ctx = resolveNewsletterContext();
  const iframe = document.createElement('iframe');
  iframe.src = buildNewsletterIframeUrl(ctx.id, location);
  iframe.loading = 'lazy';
  iframe.title = `${ctx.label} newsletter`;
  iframe.setAttribute('scrolling', 'no');
  iframe.style.border = '0';
  iframe.style.width = '100%';
  const isInline = typeof location === 'string' && location.startsWith('article');
  // article_mid renders the compact embed variant (tighter spacing, no
  // preview link), so start it shorter to limit the pre-resize footprint.
  const isCompact = location === 'article_mid';
  const initialHeight = isCompact ? 250 : (isInline ? 340 : 380);
  iframe.style.height = `${initialHeight}px`;
  iframe.style.overflow = 'hidden';

  const wrapper = document.createElement('div');
  wrapper.style.width = '100%';
  wrapper.style.margin = isInline ? '12px 0' : '0 0 12px 0';
  wrapper.style.minHeight = `${initialHeight}px`;
  wrapper.style.fontFamily =
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  wrapper.appendChild(iframe);
  return wrapper;
}

// Backwards-compatible alias
function createNewsletterModule(location) {
  return createNewsletterIframeModule(location);
}

module.exports = {
  ensureNewsletterIframeResizer,
  createNewsletterIframeModule,
  createNewsletterModule
};
